import ApplicationController from './application_controller'

// Cart Context Warning Controller
// Handles the modal for warning users when they try to add items from a different context
// (different opportunity/giving day) than their current cart

export default class extends ApplicationController {
  static targets = [
    "modal",
    "currentContextName",
    "itemCount",
    "cartTotal",
    "confirmButton",
    "switchForm",
    "clearCartInput",
    "newContextType",
    "newContextId",
    "redirectAfter"
  ]

  static values = {
    clearCartUrl: String
  }

  connect() {
    console.log('CartContextWarning controller connected')
    this.pendingAction = null
    this.pendingCallback = null

    // Initialize Bootstrap modal
    if (this.hasModalTarget && typeof bootstrap !== 'undefined') {
      this.bsModal = new bootstrap.Modal(this.modalTarget)
    }

    // Listen for custom events to show the warning
    document.addEventListener('cart:context-conflict', this.handleContextConflict.bind(this))
  }

  disconnect() {
    document.removeEventListener('cart:context-conflict', this.handleContextConflict.bind(this))
  }

  // Handle the context conflict event
  handleContextConflict(event) {
    const {
      currentContextName,
      currentItemCount,
      currentTotal,
      newContextType,
      newContextId,
      pendingAction,
      callback
    } = event.detail

    // Store pending action data
    this.pendingAction = pendingAction
    this.pendingCallback = callback

    // Update modal content
    if (this.hasCurrentContextNameTarget) {
      this.currentContextNameTarget.textContent = currentContextName || 'another event'
    }
    if (this.hasItemCountTarget) {
      this.itemCountTarget.textContent = `${currentItemCount} ${currentItemCount === 1 ? 'item' : 'items'}`
    }
    if (this.hasCartTotalTarget) {
      this.cartTotalTarget.textContent = currentTotal || '$0.00'
    }

    // Store new context info for form submission
    if (this.hasNewContextTypeTarget) {
      this.newContextTypeTarget.value = newContextType
    }
    if (this.hasNewContextIdTarget) {
      this.newContextIdTarget.value = newContextId
    }

    // Show the modal
    this.show()
  }

  show() {
    if (this.bsModal) {
      this.bsModal.show()
    } else if (this.hasModalTarget) {
      this.modalTarget.classList.add('show')
      this.modalTarget.style.display = 'block'
    }
  }

  hide() {
    if (this.bsModal) {
      this.bsModal.hide()
    } else if (this.hasModalTarget) {
      this.modalTarget.classList.remove('show')
      this.modalTarget.style.display = 'none'
    }
  }

  // User chose to keep current cart - cancel the pending action
  keepCurrentCart() {
    console.log('CartContextWarning: Keeping current cart')
    this.pendingAction = null
    this.pendingCallback = null
    this.hide()
  }

  // User chose to start new cart - clear cart and proceed with action
  async startNewCart() {
    console.log('CartContextWarning: Starting new cart')

    // Disable button to prevent double-clicks
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.disabled = true
      this.confirmButtonTarget.innerHTML = '<span class="spinner-border spinner-border-sm" role="status"></span> Clearing...'
    }

    try {
      // Clear the cart via API
      const response = await fetch('/shopping_cart/clear_for_new_context', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector("[name='csrf-token']")?.content
        },
        body: JSON.stringify({
          new_context_type: this.hasNewContextTypeTarget ? this.newContextTypeTarget.value : null,
          new_context_id: this.hasNewContextIdTarget ? this.newContextIdTarget.value : null
        })
      })

      if (response.ok) {
        const data = await response.json()

        // Hide modal
        this.hide()

        // Execute the pending callback if provided
        if (this.pendingCallback && typeof this.pendingCallback === 'function') {
          this.pendingCallback(data)
        } else if (this.pendingAction) {
          // Execute pending action (e.g., add to cart)
          this.executePendingAction()
        }

        // Dispatch success event
        document.dispatchEvent(new CustomEvent('cart:context-switched', {
          detail: { newCartId: data.cart_id }
        }))
      } else {
        console.error('Failed to clear cart:', response.statusText)
        alert('Failed to clear cart. Please try again.')
      }
    } catch (error) {
      console.error('Error clearing cart:', error)
      alert('An error occurred. Please try again.')
    } finally {
      // Reset button state
      if (this.hasConfirmButtonTarget) {
        this.confirmButtonTarget.disabled = false
        this.confirmButtonTarget.innerHTML = 'Start New Cart'
      }
    }
  }

  executePendingAction() {
    if (!this.pendingAction) return

    const { type, url, method, data } = this.pendingAction

    if (type === 'fetch') {
      fetch(url, {
        method: method || 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector("[name='csrf-token']")?.content
        },
        body: JSON.stringify(data)
      }).then(response => {
        if (response.ok) {
          // Refresh the page or update cart display
          window.location.reload()
        }
      })
    } else if (type === 'redirect') {
      window.location.href = url
    } else if (type === 'click') {
      // Re-trigger the original click
      const element = document.querySelector(this.pendingAction.selector)
      if (element) {
        element.click()
      }
    }

    this.pendingAction = null
  }
}
